import React from 'react'
import PropTypes from 'prop-types'
import { Flex, Box, ScreenSwitch, Hide, PromoBanner, RemoteConfigConsumer, Copy } from '@elparking/components'
import ErrorBoundary from 'commons/js/components/ErrorBoundary'
import currentCountry from 'commons/js/country'
import Sidebar, { AnimatedSidebar } from '../../ui/Sidebar'
import SearchFormContainer from '../../ui/SearchFormContainer'
import ListContainer from '../../ui/ListContainer'
import MapContainer from '../../ui/MapContainer'
import Content from '../../ui/Content'
import List from '../../components/List'
import SearchForm from '../../components/SearchForm'
import ChargingPointCard from '../../components/ChargingPointCard'
import ChargingPointDetail from '../../components/ChargingPointDetail'
import BannerDownloadApp from '../../components/BannerDownloadApp'
import { FormattedMessage, injectIntl } from 'react-intl'

import MapLoader from '../../ui/MapLoader'
import Map from '../../components/Map'
import FilterContainer from '../../components/FilterContainer'
import ChargingPointSpeedFilters from '../../components/ChargingPointSpeedFilters'
import ChargingPointPlugTypeFilters from '../../components/ChargingPointPlugTypeFilters'
import {
    getLatitude,
    getLongitude,
} from './searchLenses'
import {
    CHARGING_POINT_SPEED_FILTER,
    CHARGING_POINT_PLUG_TYPE_FILTER,
    SPEED_CHARGING_FAST_DESCRIPTION,
} from 'commons/js/constants'
import { getActiveSpeedFilters, getMinimumActiveSpeedFilterText } from 'electric-charge-search-map/js/util/chargingPointFilter'

const FullSizeContainer = <div style={{height: '100%', width: '100%'}} />
const loadingElement = <MapLoader />

function ChargingPoints ({
    setFormSearch,
    search,
    chargingPoints,
    computeDistanceTo,
    searchPoint,
    autoCenter,
    isLoading,
    center,
    markedChargingPointId,
    selectedChargingPointId,
    setMapSearch,
    markChargingPoint,
    markAndCenterChargingPoint,
    selectChargingPoint,
    disableAutoCenter,
    activeFilter,
    setActiveFilter,
    filters,
    setFilters,
    intl,
}) {
    const { place } = search
    const markedChargingPoint = chargingPoints.find((chargingPoint) => chargingPoint.id === markedChargingPointId)

    const activeSpeedFilters = getActiveSpeedFilters(filters.chargingPointSpeed)
    const slowestSelectedChargeType = activeSpeedFilters.length > 0 ? getMinimumActiveSpeedFilterText(activeSpeedFilters) : null

    const shouldShowExplicativeMessage = chargingPoints.length > 0 && activeSpeedFilters.length === 1 && activeSpeedFilters[0].value !== SPEED_CHARGING_FAST_DESCRIPTION
    return (
        <Flex w='100vw' flexDirection='column'>
            <ErrorBoundary>
                <RemoteConfigConsumer value='promo_banner_charging_point_search'>
                    {({ active = false,
                        text,
                        link_text: linkText,
                        link_url: linkUrl,
                        enable_on_mobile: showOnMobile,
                        campaign,
                    }) =>
                        active && (
                            <Box w='100%'>
                                <PromoBanner
                                  text={text}
                                  linkText={linkText}
                                  linkUrl={linkUrl}
                                  showOnMobile={showOnMobile}
                                  campaign={campaign}
                                  id={`promo-bar-${currentCountry}`}
                                />
                            </Box>
                        )
                    }
                </RemoteConfigConsumer>
            </ErrorBoundary>
            <Flex flex='1' style={{ overflow: 'hidden' }}>
                <Sidebar>
                    <SearchFormContainer>
                        <SearchForm
                          onSearch={setFormSearch}
                          place={place}
                          activeFilter={activeFilter}
                          filters={filters}
                          setActiveFilter={setActiveFilter}
                        />
                    </SearchFormContainer>
                    <ListContainer>
                        {activeFilter && <FilterContainer onClose={() => { setFilters(filters) }}>
                            {activeFilter === CHARGING_POINT_SPEED_FILTER && <ChargingPointSpeedFilters
                              filters={filters.chargingPointSpeed}
                              setFilters={(speedFilters) => { setFilters({ chargingPointSpeed: speedFilters }) }}
                            />}
                            {activeFilter === CHARGING_POINT_PLUG_TYPE_FILTER && <ChargingPointPlugTypeFilters
                              filters={filters.chargingPointPlugType}
                              setFilters={(plugTypeFilters) => { setFilters({ chargingPointPlugType: plugTypeFilters }) }}
                            />}
                        </FilterContainer>}
                        {!activeFilter && (
                            <Box background='white' minHeight='100%'>
                                <Hide xs sm md>
                                    <BannerDownloadApp text={<FormattedMessage defaultMessage='Descarga la app para reservar los puntos de carga' />} />
                                </Hide>

                                {shouldShowExplicativeMessage && <Box padding='1em' dataTest='speed-filter-message'>
                                    <Copy fontWeight='regular'>
                                        <FormattedMessage
                                          defaultMessage='Los puntos de carga muestran la opción de carga más rápida disponible, pero también tienen la opción de carga {slowestSelectedChargeType}.'
                                          values={{ slowestSelectedChargeType: intl.formatMessage(slowestSelectedChargeType).toLowerCase() }}
                                        />
                                    </Copy>
                                </Box>}
                                <List
                                  onSelect={selectChargingPoint}
                                  onMarkAndCenter={markAndCenterChargingPoint}
                                  computeDistanceTo={computeDistanceTo}
                                  chargingPoints={chargingPoints}
                                  cardComponent={ChargingPointCard}
                                  selected={selectedChargingPointId}
                                  marked={markedChargingPointId}
                                />
                            </Box>
                        )}
                    </ListContainer>
                </Sidebar>
                <AnimatedSidebar isOpen={markedChargingPoint}>
                    <ChargingPointDetail
                      computeDistanceTo={computeDistanceTo}
                      chargingPoint={markedChargingPoint}
                      onClose={markChargingPoint}
                />
                </AnimatedSidebar>
                <Content>
                    <MapContainer isMarked={!!markedChargingPointId && markedChargingPoint}>
                        <ScreenSwitch
                          medium={Map}
                          searchPoint={searchPoint}
                          autoCenter={autoCenter}
                          selected={selectedChargingPointId}
                          marked={markedChargingPointId}
                          latitude={getLatitude(search)}
                          longitude={getLongitude(search)}
                          center={center}
                          chargingPoints={chargingPoints}
                          isLoading={isLoading}
                          containerElement={FullSizeContainer}
                          mapElement={FullSizeContainer}
                          loadingElement={loadingElement}
                          onSearch={setMapSearch}
                          onMark={markChargingPoint}
                          onMarkAndCenterChargingPoint={markAndCenterChargingPoint}
                          disableAutoCenter={disableAutoCenter}
                        />
                    </MapContainer>
                </Content>
            </Flex>
        </Flex>
    )
}

ChargingPoints.propTypes = {
    search: PropTypes.shape({
        place: PropTypes.shape({
            place_id: PropTypes.string,
        }),
    }),
    computeDistanceTo: PropTypes.func,
    setFormSearch: PropTypes.func,
    markChargingPoint: PropTypes.func,
    selectChargingPoint: PropTypes.func,
    markAndCenterChargingPoint: PropTypes.func,
    disableAutoCenter: PropTypes.func,
    setMapSearch: PropTypes.func,
    chargingPoints: PropTypes.array,
    searchPoint: PropTypes.object,
    center: PropTypes.object,
    autoCenter: PropTypes.bool,
    isLoading: PropTypes.bool,
    selectedChargingPointId: PropTypes.number,
    markedChargingPointId: PropTypes.number,
    activeFilter: PropTypes.string,
    setActiveFilter: PropTypes.func,
    filters: PropTypes.object,
    setFilters: PropTypes.func,
    intl: PropTypes.object,
}

export default injectIntl(ChargingPoints)
